import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Sidebar.css";
import { useNavigate } from "react-router-dom";
import { FaAngleUp, FaAngleDown } from "react-icons/fa";
import { RiEarthquakeLine } from "react-icons/ri";
import { MdOutlineLandslide } from "react-icons/md";
import {
    Zap,
    Flame,
    Car,
    GitBranch,
    TrendingUp,
    Waves,
    History,
    AlertTriangle,
    Share2,
    FileText,
    Map,
    Database,
    TrendingDown,
    CloudRain,
    Cloud,
    Flag,
    BarChart3,
    LayoutDashboard,
    Truck,
    Route,
    Cross,
    Info,
    Settings2,
    Tornado,
    IdCard,
    UserCircle,
    ChartLine,
    LayoutDashboardIcon,
    UserCog,
    BadgeInfo,
    SatelliteDish,
    Globe,
    Radio,
    TrendingUpDown,
} from "lucide-react";

const Sidebar = ({ isCollapsed, isMobileOpen, onNavigate }) => {
    const navigate = useNavigate();
    const [activeItem, setActiveItem] = useState(null);
    const [activeSubItem, setActiveSubItem] = useState(null);
    const [dropdownStates, setDropdownStates] = useState({});
    const userRole = localStorage.getItem("role"); // 'superadmin', 'admin', 'user', or a custom role code — display/analytics only, not used for access checks below
    const isSuperAdmin = userRole === 'superadmin' || localStorage.getItem('is_superuser') === 'true';
    let userPermissions = [];
    try {
        userPermissions = JSON.parse(localStorage.getItem('permissions') || '[]');
    } catch (e) {
        userPermissions = [];
    }

    // Fully dynamic RBAC: menu item visibility is driven entirely by the
    // permission codes the backend granted this user's role (cached in
    // localStorage('permissions') by LoginPage.js at login) — there is no
    // hardcoded role-code list anywhere in this menu definition. `requiredPermission`
    // gates a single item/group; `anyOfPermissions` is for group headers whose
    // children each require a different permission (visible if the user holds
    // at least one of them). Items with neither are visible to everyone
    // logged in. A Super Admin always sees everything.
    const hasAccess = (requiredPermission, anyOfPermissions) => {
        if (isSuperAdmin) return true;
        if (requiredPermission) return userPermissions.includes(requiredPermission);
        if (anyOfPermissions) return anyOfPermissions.some((code) => userPermissions.includes(code));
        return true; // no restriction, visible to all
    };

    // Menu structure with requiredPermission/anyOfPermissions on subItems.
    // Order below is the source of truth for both the sidebar and the
    // Roles & Permissions screen (see PERMISSION_DISPLAY_ORDER in
    // RolesPermissions.js, which mirrors this same module/item sequence).
    //
    // DEFAULT_ACCESS_PERMISSION_CODES (backend: login/models.py) lists the
    // permission codes that are granted to every role automatically and
    // cannot be revoked - "Dashboard" items and the marked items below are
    // always visible regardless of a user's assigned role.
    const menuItems = [
        {
            icon: <TrendingUpDown className="menuitemsicons" />,
            name: "Forecast",
            path: "/",
        },
        {
            icon: <Globe className="menuitemsicons" />,
            name: "Overview",
            path: "/Overview",
            requiredPermission: "dashboard_overview",
        },
        {
            icon: <Radio className="menuitemsicons" />,
            name: "Active Events",
            path: "/ActiveEvents",
            requiredPermission: "dashboard_active_events",
        },
       
        {
            icon: <SatelliteDish className="menuitemsicons" />,
            name: "Weather Station",
            path: "/WeatherStation",
            requiredPermission: "weather_station",
        },
        {
            icon: <Tornado className="menuitemsicons" />,
            name: "Cyclone",
            subItems: [
                { name: "Disturbance Track", path: "/Cyclone/DisturbanceTrack", icon: <GitBranch className="menuitemsicons" /> , requiredPermission: "cyclone_disturbance_track" },
                { name: "Ocean State", path: "/Cyclone/OceanState", icon: <Waves className="menuitemsicons" /> , requiredPermission: "cyclone_ocean_state" },
                { name: "Historical Cyclone", path: "/Cyclone/HistoricalCyclone", icon: <History className="menuitemsicons" /> , requiredPermission: "cyclone_historical_cyclone" },
                { name: "Advisory", path: "/Cyclone/AdvisoryCyclone", icon: <BadgeInfo className="menuitemsicons" /> , requiredPermission: "cyclone_advisory" },
                { name: "Report List", path: "/Cyclone/Reports", icon: <FileText className="menuitemsicons" /> , requiredPermission: "cyclone_report_list" },
                {
                    name: "Data Panel",
                    icon: <Database className="menuitemsicons" />,
                    anyOfPermissions: ["cyclone_data_panel_advisory", "cyclone_data_panel_felt_report"],
                    dropdownItems: [
                        { name: "Cyclone Advisory", path: "/Cyclone/DataPanel/Cyclone_advisory", icon: <BadgeInfo className="menuitemsicons" />, requiredPermission: "cyclone_data_panel_advisory" },
                        { name: "Felt Report", path: "/Cyclone/DataPanel/FeltReport", icon: <Flag className="menuitemsicons" />, requiredPermission: "cyclone_data_panel_felt_report" },
                    ],
                },
            ],
        },
        {
            icon: <MdOutlineLandslide className="menuitemsicons" style={{ fontSize: '27px' }} />,
            name: "Landslide",
            subItems: [
                { name: "Forecast", path: "/Landslide/Forecast", icon: <TrendingDown className="menuitemsicons" /> , requiredPermission: "landslide_forecast" },
                { name: "Projections", path: "/Landslide/Projections", icon: <TrendingUp className="menuitemsicons" /> , requiredPermission: "landslide_projections" },
                { name: "Historical Data", path: "/Landslide/HistoricalData", icon: <History className="menuitemsicons" /> , requiredPermission: "landslide_historical_data" },
                {
                    name: "Geological Terrain",
                    icon: <Map className="menuitemsicons" />,
                    anyOfPermissions: ["landslide_geological_terrain_land_cover", "landslide_geological_terrain_soil_texture"],
                    dropdownItems: [
                        { name: "Land Cover", path: "/Landslide/GeologicalTerrainData/LandCover", icon: <Map className="menuitemsicons" /> , requiredPermission: "landslide_geological_terrain_land_cover" },
                        { name: "Soil Texture", path: "/Landslide/GeologicalTerrainData/SoilTexture", icon: <Map className="menuitemsicons" /> , requiredPermission: "landslide_geological_terrain_soil_texture" },
                    ],
                },
                {
                    name: "Susceptibility",
                    icon: <Map className="menuitemsicons" />,
                    anyOfPermissions: ["landslide_susceptibility_lsm", "landslide_susceptibility_seasonal_wise"],
                    dropdownItems: [
                        { name: "LSM", path: "/Landslide/Susceptibility/LSM", icon: <Map className="menuitemsicons" /> , requiredPermission: "landslide_susceptibility_lsm" },
                        { name: "Seasonal Wise", path: "/Landslide/Susceptibility/SeasonalWise", icon: <Map className="menuitemsicons" /> , requiredPermission: "landslide_susceptibility_seasonal_wise" },
                    ],
                },
                 { name: "Advisory", path: "/Landslide/Advisory", icon: <BadgeInfo className="menuitemsicons" /> , requiredPermission: "landslide_advisory" },
                { name: "Report List", path: "/Landslide/Reports", icon: <FileText className="menuitemsicons" /> , requiredPermission: "landslide_report_list" },
                {
                    name: "Data Panel",
                    icon: <Database className="menuitemsicons" />,
                    anyOfPermissions: ["landslide_data_panel_felt_report", "landslide_data_panel_advisory"],
                    dropdownItems: [
                        { name: "Felt Report", path: "/Landslide/DataPanel/FeltReport", icon: <Flag className="menuitemsicons" /> , requiredPermission: "landslide_data_panel_felt_report" },
                        { name: "Landslide Advisory", path: "/Landslide/DataPanel/Landslide_advisory", icon: <BadgeInfo className="menuitemsicons" /> , requiredPermission: "landslide_data_panel_advisory" },
                    ],
                },
            ],
        },
        {
            icon: <RiEarthquakeLine className="menuitemsicons" style={{ fontSize: '27px' }} />,
            name: "Earthquake",
            subItems: [
                { name: "Recent Earthquake", path: "/Earthquake/RecentEarthquake", icon: <RiEarthquakeLine className="menuitemsicons" style={{ fontSize: '27px' }} /> , requiredPermission: "earthquake_recent_earthquake" },
                { name: "Statistics", path: "/Earthquake/Statistics", icon: <BarChart3 className="menuitemsicons" /> , requiredPermission: "earthquake_statistics" },
                { name: "Advisory", path: "/Earthquake/Advisory", icon: <BadgeInfo className="menuitemsicons" /> , requiredPermission: "earthquake_advisory" },
                { name: "Report List", path: "/Earthquake/Reports", icon: <FileText className="menuitemsicons" /> , requiredPermission: "earthquake_report_list" },
                {
                    name: "Exposure",
                    icon: <Cross className="menuitemsicons" />,
                    requiredPermission: "earthquake_exposure_health_centers",
                    dropdownItems: [
                        { name: "Health Centers", path: "/Earthquake/HealthCenters", icon: <Cross className="menuitemsicons" /> , requiredPermission: "earthquake_exposure_health_centers" },
                    ],
                },
                {
                    name: "Data Panel",
                    icon: <Database className="menuitemsicons" />,
                    anyOfPermissions: [
                        "earthquake_data_panel_advisory", "earthquake_data_panel_events",
                        "earthquake_felt_report", "earthquake_data_panel_settings",
                    ],
                    dropdownItems: [
                        { name: "Advisory", path: "/Earthquake/DataPanel/Advisory", icon: <BadgeInfo className="menuitemsicons" /> , requiredPermission: "earthquake_data_panel_advisory" },
                        { name: "Felt Report", path: "/Earthquake/FeltReport", icon: <Flag className="menuitemsicons" /> , requiredPermission: "earthquake_felt_report" },
                        { name: "Earthquake Events", path: "/Earthquake/DataPanel/Events", icon: <FileText className="menuitemsicons" /> , requiredPermission: "earthquake_data_panel_events" },
                        { name: "Settings", path: "/Earthquake/DataPanel/Settings", icon: <Settings2 className="menuitemsicons" /> , requiredPermission: "earthquake_data_panel_settings" },
                    ],
                },
                
            ],
        },
        {
            icon: <CloudRain className="menuitemsicons" />,
            name: "Flood",
            subItems: [
                { name: "Dashboard", path: "/Flood/Dashboard", icon: <LayoutDashboardIcon className="menuitemsicons" /> , requiredPermission: "flood_dashboard" },
                { name: "Flash Flood Threat", path: "/Flood/FlashFloodThreat", icon: <Waves className="menuitemsicons" /> , requiredPermission: "flood_flash_flood_threat" },
                { name: "Analysis", path: "/Flood/Analysis", icon: <ChartLine className="menuitemsicons" /> , requiredPermission: "flood_analysis" },
                {
                    name: "Exposure",
                    icon: <Waves className="menuitemsicons" />,
                    requiredPermission: "flood_exposure_hydrological_data",
                    dropdownItems: [
                        { name: "Hydrological Data", path: "/Flood/HydrologicalData", icon: <Waves className="menuitemsicons" /> , requiredPermission: "flood_exposure_hydrological_data" },
                    ],
                },
                { name: "Flood Advisory View", path: "/Flood/FloodAdvisory", icon: <BadgeInfo className="menuitemsicons" /> , requiredPermission: "flood_advisory_view" },
                { name: "Report List", path: "/Flood/Reports", icon: <FileText className="menuitemsicons" /> , requiredPermission: "flood_report_list" },
                {
                    name: "Data Panel",
                    icon: <Database className="menuitemsicons" />,
                    anyOfPermissions: ["flood_data_panel_advisory", "flood_data_panel_felt_report"],
                    dropdownItems: [
                        { name: "Advisory", path: "/Flood/Datapanel/Advisory", icon: <BadgeInfo className="menuitemsicons" /> , requiredPermission: "flood_data_panel_advisory" },
                        { name: "Felt Report", path: "/Flood/DataPanel/FeltReport", icon: <Flag className="menuitemsicons" /> , requiredPermission: "flood_data_panel_felt_report" },
                    ],
                },
            ],
        },
        {
            icon: <Flame className="menuitemsicons" />,
            name: "Fire Risk",
            subItems: [
                // Kept first per explicit request ("keep forecast first").
                { name: "Forecast", path: "/Firerisk/Forecast", icon: <TrendingUp className="menuitemsicons" /> , requiredPermission: "firerisk_forecast" },
                { name: "FIRMS", path: "/Firerisk/Firms", icon: <SatelliteDish className="menuitemsicons" /> , requiredPermission: "firerisk_firms" },
                {
                    name: "Exposure",
                    icon: <Route className="menuitemsicons" />,
                    anyOfPermissions: ["firerisk_exposure_infrastructure", "firerisk_exposure_population", "firerisk_exposure_network"],
                    dropdownItems: [
                        { name: "Infrastructure", path: "/Firerisk/Infrastructure", icon: <Map className="menuitemsicons" /> , requiredPermission: "firerisk_exposure_infrastructure" },
                        { name: "Population", path: "/Firerisk/Population", icon: <Map className="menuitemsicons" /> , requiredPermission: "firerisk_exposure_population" },
                        { name: "Network", path: "/Firerisk/Network", icon: <Route className="menuitemsicons" /> , requiredPermission: "firerisk_exposure_network" },
                    ],
                },
                { name: "Advisory", path: "/Firerisk/Advisory", icon: <BadgeInfo className="menuitemsicons" /> , requiredPermission: "firerisk_advisory" },
                { name: "Report List", path: "/Firerisk/Reports", icon: <FileText className="menuitemsicons" /> , requiredPermission: "firerisk_report_list" },
                {
                    name: "Data Panel",
                    icon: <Database className="menuitemsicons" />,
                    anyOfPermissions: ["firerisk_data_panel_felt_report", "firerisk_data_panel_advisory"],
                    dropdownItems: [
                        { name: "Felt Report", path: "/Firerisk/DataPanel/FeltReport", icon: <Flag className="menuitemsicons" /> , requiredPermission: "firerisk_data_panel_felt_report" },
                        { name: "Fire Risk Advisory", path: "/Firerisk/DataPanel/Firerisk_advisory", icon: <BadgeInfo className="menuitemsicons" /> , requiredPermission: "firerisk_data_panel_advisory" },
                    ],
                },
            ],
        },

        {
            icon: <Zap className="menuitemsicons" />,
            name: "Lightning",
            subItems: [
                { name: "Nowcast", path: "/Lightning/Nowcast", icon: <Zap className="menuitemsicons" /> , requiredPermission: "lightning_nowcast" },
                { name: "Flash Density", path: "/Lightning/FlashDensity", icon: <Cloud className="menuitemsicons" /> , requiredPermission: "lightning_flash_density" },
                { name: "Statistics", path: "/Lightning/Statistics", icon: <BarChart3 className="menuitemsicons" /> , requiredPermission: "lightning_statistics" },
                { name: "Advisory", path: "/Lightning/Lightningadvisory", icon: <BadgeInfo className="menuitemsicons" /> , requiredPermission: "lightning_advisory" },
                { name: "Report List", path: "/Lightning/Reports", icon: <FileText className="menuitemsicons" /> , requiredPermission: "lightning_report_list" },
                {
                    name: "Data Panel",
                    icon: <Database className="menuitemsicons" />,
                    anyOfPermissions: ["lightning_data_panel_advisory", "lightning_data_panel_reports"],
                    dropdownItems: [
                        { name: "Advisory", path: "/Lightning/Advisory", icon: <BadgeInfo className="menuitemsicons" /> , requiredPermission: "lightning_data_panel_advisory" },
                        { name: "Reports", path: "/Lightning/DataPanel/Reports", icon: <Flag className="menuitemsicons" /> , requiredPermission: "lightning_data_panel_reports" },
                    ],
                },

            ],
        },
                {
            icon: <Car className="menuitemsicons" />,
            name: "Road Accident",
            subItems: [
                { name: "Dashboard", path: "/Roadaccident/Dashboard", icon: <LayoutDashboard className="menuitemsicons" /> , requiredPermission: "roadaccident_dashboard" },
                { name: "Assistance On Move", path: "/Roadaccident/AssistanceOnMove", icon: <Truck className="menuitemsicons" /> , requiredPermission: "roadaccident_assistance_on_move" },
                { name: "Hospitals", path: "/Roadaccident/Hospitals", icon: <Cross className="menuitemsicons" /> , requiredPermission: "roadaccident_hospitals" },
                { name: "Road Networks", path: "/Roadaccident/RoadNetworks", icon: <Route className="menuitemsicons" /> , requiredPermission: "roadaccident_road_networks" },
                { name: "Advisory", path: "/Roadaccident/RoadaccidentAdvisory", icon: <AlertTriangle className="menuitemsicons" /> , requiredPermission: "roadaccident_advisory" },
                { name: "Report List", path: "/Roadaccident/Reports", icon: <FileText className="menuitemsicons" />, requiredPermission: "roadaccident_report_list" },
                {
                    name: "Data Panel",
                    icon: <Database className="menuitemsicons" />,
                    anyOfPermissions: ["roadaccident_data_panel_advisory", "roadaccident_data_panel_reports"],
                    dropdownItems: [
                        { name: "Advisory", path: "/Roadaccident/Advisory", icon: <BadgeInfo className="menuitemsicons" /> , requiredPermission: "roadaccident_data_panel_advisory" },
                        { name: "Reports", path: "/Roadaccident/DataPanel/Reports", icon: <Flag className="menuitemsicons" /> , requiredPermission: "roadaccident_data_panel_reports" },
                    ],
                },
            ],
        },
        {
            icon: <Share2 className="menuitemsicons" />,
            name: "Dissemination",
            subItems: [
                { name: "Dissemination", path: "/Disseminate", icon: <Share2 className="menuitemsicons" />, requiredPermission: "dissemination_broadcast" },
                { name: "Officials Info Form", path: "/Dissemination/Staffinfo/OfficialsInfoForm", icon: <UserCircle className="menuitemsicons" />, requiredPermission: "dissemination_staff_info_form" },
                { name: "Officials Profile", path: "/Dissemination/Staffinfo/OfficialsProfile", icon: <IdCard className="menuitemsicons" />, requiredPermission: "dissemination_staff_profile" },
            ]
        },
        {
            icon: <UserCog className="menuitemsicons" />,
            name: "Administration",
            anyOfPermissions: [
                "admin_user_management", "admin_roles_permissions", "admin_data_management",
                "admin_system_monitoring", "admin_forecast_value_addition", "admin_situation_reports",
                "admin_report_approval",
            ],
            subItems: [
                { name: "User Management", path: "/AdminPanel/UserManagement", icon: <UserCircle className="menuitemsicons" />, requiredPermission: "admin_user_management" },
                { name: "Roles & Permissions", path: "/AdminPanel/RolesPermissions", icon: <UserCircle className="menuitemsicons" />, requiredPermission: "admin_roles_permissions" },
                { name: "Data Management", path: "/AdminPanel/DataManagement", icon: <UserCircle className="menuitemsicons" />, requiredPermission: "admin_data_management" },
                { name: "Forecast Value Addition", path: "/AdminPanel/ForecastValueAddition", icon: <CloudRain className="menuitemsicons" />, requiredPermission: "admin_forecast_value_addition" },
                { name: "System Monitoring", path: "/AdminPanel/SystemMonitoring", icon: <Database className="menuitemsicons" />, requiredPermission: "admin_system_monitoring" },
                { name: "Situation Reports", path: "/AdminPanel/IncidentReports", icon: <FileText className="menuitemsicons" />, requiredPermission: "admin_situation_reports" },
                { name: "Report Approval", path: "/AdminPanel/ReportApproval", icon: <AlertTriangle className="menuitemsicons" />, requiredPermission: "admin_report_approval" },
            ]
        },
        {
            icon: <Info className="menuitemsicons" />,
            name: "About",
            path: "/About",
        },
    ];

    // A group (a subItem with dropdownItems, e.g. "Data Panel") is only shown
    // if at least one of its dropdown items is accessible — and when shown,
    // only the accessible dropdown items are listed. If none are accessible,
    // the whole group header is dropped, not just its (now-empty) contents.
    // The same rule cascades up to top-level items that hold subItems.
    const filteredMenuItems = menuItems
        .map(item => {
            if (!item.subItems) return item;

            const filteredSubItems = item.subItems
                .map(subItem => {
                    if (!subItem.dropdownItems) return subItem;

                    const filteredDropdownItems = subItem.dropdownItems.filter(ddItem => hasAccess(ddItem.requiredPermission));
                    return { ...subItem, dropdownItems: filteredDropdownItems };
                })
                .filter(subItem =>
                    subItem.dropdownItems
                        ? subItem.dropdownItems.length > 0
                        : hasAccess(subItem.requiredPermission, subItem.anyOfPermissions)
                );

            return { ...item, subItems: filteredSubItems };
        })
        .filter(item =>
            item.subItems ? item.subItems.length > 0 : hasAccess(item.requiredPermission, item.anyOfPermissions)
        );

    // Then render filteredMenuItems instead of menuItems


    // Retrieve saved states on load
    useEffect(() => {
        const savedActiveItem = localStorage.getItem("activeItem");
        const savedActiveSubItem = localStorage.getItem("activeSubItem");
        const savedDropdownStates = JSON.parse(localStorage.getItem("dropdownStates"));
        if (savedActiveItem) setActiveItem(savedActiveItem);
        if (savedActiveSubItem) setActiveSubItem(savedActiveSubItem);
        if (savedDropdownStates) setDropdownStates(savedDropdownStates);
    }, []);

    const handleMenuClick = (item) => {
        if (item.subItems) {
            setActiveItem(activeItem === item.name ? null : item.name);
        } else {
            setActiveItem(item.name);
            setActiveSubItem(null);
            if (item.path) {
                navigate(item.path);
                if (onNavigate) onNavigate();
            }
        }
        localStorage.setItem("activeItem", item.name);
        localStorage.setItem("activeSubItem", null);
    };

    const handleSubItemClick = (subItem, event) => {
        event.stopPropagation();
        if (subItem.path) {
            setActiveSubItem(subItem.path);
            navigate(subItem.path);
            if (onNavigate) onNavigate();
            localStorage.setItem("activeSubItem", subItem.path);
            // Collapse all dropdowns on subItem click
            setDropdownStates(prev => {
                const newState = { ...prev };
                Object.keys(newState).forEach(key => {
                    if (newState[key]) newState[key] = false;
                });
                localStorage.setItem("dropdownStates", JSON.stringify(newState));
                return newState;
            });
        } else if (subItem.dropdownItems) {
            handleDropdownToggle(subItem.name, event, true);
        }
    };

    const handleDropdownToggle = (itemName, event, isSubitem = false) => {
        event.stopPropagation();
        setDropdownStates((prevStates) => {
            const newState = { ...prevStates };
            if (isSubitem && newState[itemName]) {
                newState[itemName] = true;
            } else {
                Object.keys(newState).forEach((key) => {
                    if (key !== itemName) newState[key] = false;
                });
                newState[itemName] = !newState[itemName];
            }
            localStorage.setItem("dropdownStates", JSON.stringify(newState));
            return newState;
        });
    };

    const handleSubSubItemClick = (parentItemName, subSubItem) => {
        setActiveSubItem(subSubItem.path);
        navigate(subSubItem.path);
        if (onNavigate) onNavigate();
        localStorage.setItem("activeSubItem", subSubItem.path);
        setDropdownStates((prevStates) => ({
            ...prevStates,
            [parentItemName]: true,
        }));
    };

    const getItemClass = (item) => (activeItem === item.name ? "active-item" : "");
    const getSubItemClass = (subItemPath) => (activeSubItem === subItemPath ? "active-subitem" : "");

    return (
        <div
            className={`menu menu_SIkG bg-white text-dark vh-100 position-fixed ${isCollapsed ? "collapsed" : ""} ${isMobileOpen ? "mobile-open" : ""}`}
            style={{ zIndex: 1001 }}
        >
            <div className="p-1">
                <ul className="list-unstyled">
                    {filteredMenuItems.map((item) => (
                        <li key={item.name} className={`menu-item position-relative ${getItemClass(item)}`}>
                            <div
                                className="menu-link d-flex align-items-center position-relative"
                                onClick={() => handleMenuClick(item)}
                            >
                                <span className="menu-icon-wrapper position-relative">
                                    {item.icon}
                                    <span className="menu-name-tooltip">{item.name}</span>
                                </span>
                                {!isCollapsed && item.name}
                            </div>
                        </li>
                    ))}
                </ul>

                {filteredMenuItems.map(
                    (item) =>
                        !isCollapsed &&
                        activeItem === item.name &&
                        item.subItems && (
                            <div key={`sub-menu-${item.name}`} className="sub-menu">
                                <div className="submenu-title">{item.name}</div>
                                <ul className="list-unstyled">
                                    {item.subItems.map((subItem) =>
                                        hasAccess(subItem.requiredPermission) ? (
                                            <li
                                                key={subItem.name}
                                                className={`sub-item ${getSubItemClass(subItem.path)}`}
                                                onClick={(event) => handleSubItemClick(subItem, event)}
                                            >
                                                {!subItem.dropdownItems ? (
                                                    <div className="d-flex align-items-center">
                                                        {subItem.icon}
                                                        <span className="ms-2">{subItem.name}</span>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <div
                                                            className="dropdown-item d-flex align-items-center justify-content-between"
                                                            onClick={(event) => handleDropdownToggle(subItem.name, event)}
                                                        >
                                                            <div className="d-flex align-items-center">
                                                                {subItem.icon}
                                                                <span className="ms-2">{subItem.name}</span>
                                                            </div>
                                                            {dropdownStates[subItem.name] ? <FaAngleUp /> : <FaAngleDown />}
                                                        </div>
                                                        {dropdownStates[subItem.name] && (
                                                            <ul className="dropdown-list list-unstyled pl-3">
                                                                {subItem.dropdownItems
                                                                    .filter(ddItem => hasAccess(ddItem.requiredPermission))
                                                                    .map((dropdownItem) => (
                                                                        <li
                                                                            key={dropdownItem.name}
                                                                            className={`dropdown-sub-item ${getSubItemClass(dropdownItem.path)}`}
                                                                            onClick={() => handleSubSubItemClick(subItem.name, dropdownItem)}
                                                                        >
                                                                            <div className="d-flex align-items-center">
                                                                                {dropdownItem.icon}
                                                                                <span className="ms-2">{dropdownItem.name}</span>
                                                                            </div>
                                                                        </li>
                                                                    ))}
                                                            </ul>
                                                        )}
                                                    </div>
                                                )}
                                            </li>
                                        ) : null
                                    )}
                                </ul>
                                <div className="submen-end"></div>
                            </div>
                        )
                )}
            </div>
        </div>
    );
};

export default Sidebar;


